import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {HeaderService} from '../../../core/services/header.service';
import {Title} from '@angular/platform-browser';
import {BaseComponent} from '../../../core/base.component';
import {AuthService} from '../../../core/security/auth.service';
import {FaxReportsService} from '../fax-reports.service';
import {appColors} from '../../../core/styles/colors';
import {Subscription} from 'rxjs';
import {DateTime} from 'luxon';
import {ReportRequest} from '../../../api/models/report-request';
import {FaxPhoneNumberModel, FaxSummaryReportModel, FaxTopNumbersReportModel} from '../../../api/models';
import {MatDialog} from '@angular/material/dialog';
import {FilterOptionModel} from '../../../dialogs/grid-filter/filter-option.model';
import {PhoneNumbersUtils} from '../../../core/utilities/phone-numbers-helpers';
import {MatFaxChartZoomDialogComponent} from './mat-fax-chart-zoom-dialog.component';
import {FaxLogsDialogComponent} from '../fax-logs/fax-logs-dialog.component';
import { FlexModule } from '@angular/flex-layout';
import { GridFilterComponent } from '../../../dialogs/grid-filter/grid-filter.component';
import { FaxSummaryCardComponent } from './summary-card.component';
import { FaxChartByTimeComponent } from './chart-by-time.component';
import { NgFor, NgIf } from '@angular/common';
import { PhoneNumberPipe } from '../../../core/pipes/phone-number.pipe';

@Component({
    selector: 'app-fax-reports-summary',
    templateUrl: './summary.component.html',
    styleUrls: ['./summary.component.scss'],
    standalone: true,
    imports: [
        FlexModule,
        GridFilterComponent,
        FaxSummaryCardComponent,
        FaxChartByTimeComponent,
        NgFor,
        NgIf,
        PhoneNumberPipe,
    ],
})
export class FaxReportsSummaryComponent extends BaseComponent implements OnInit, OnDestroy {

  filterOptions: FilterOptionModel[] = [];
  additionalFilter: any = {};

  timeSeriesType: 'line' | 'bar' = 'bar';
  colors = appColors;
  buttonSubmitPromise: Promise<void>;
  summarySubscription: Subscription;
  summaryByDaySubscription: Subscription;
  summaryByPhoneNumberSubscription: Subscription;
  messagingPhoneNumbersSubscription: Subscription;
  grandTotals: FaxSummaryReportModel = {
    outboundFax: 0,
    inboundFax: 0,
    failedFax: 0,
    totalFax: 0,
  };
  timeSeriesTotals = [];
  topNumbersByAll: FaxTopNumbersReportModel[] = [];
  topOutgoingNumbersByAll: FaxTopNumbersReportModel[] = [];
  topIncomingNumbersByAll: FaxTopNumbersReportModel[] = [];
  faxPhoneNumbers: FaxPhoneNumberModel[] = [];

  constructor(route: ActivatedRoute, headerService: HeaderService, titleService: Title, authService: AuthService,
              public dialog: MatDialog,
              public reportsService: FaxReportsService) {
    super(route, headerService, titleService, authService);
  }

  async ngOnInit() {
    await super.ngOnInit();

    this.summarySubscription = this.reportsService.summaryList.subscribe((data) => {
      this.grandTotals = data || {
        outboundFax: 0,
        inboundFax: 0,
        failedFax: 0,
        totalFax: 0,
      };
    });

    this.summaryByDaySubscription = this.reportsService.summaryListByDay.subscribe((data) => {
      let maxDate = DateTime.fromISO('1790-01-01');
      this.timeSeriesTotals = data.map((item) => {

        const localDateTime = DateTime.fromISO(item.day); // .setZone(Settings.defaultZone);

        if (localDateTime > maxDate) {
          maxDate = localDateTime;
        }

        return {
          day: new Date(item.day), // DateUtils.localDateFromDateTimeOnlyDate(localDateTime),
          inboundFax: item.inboundFax,
          outboundFax: item.outboundFax,
          failedFax: item.failedFax,
        };
      });
    });

    this.summaryByPhoneNumberSubscription = this.reportsService.summaryListByPhoneNumber.subscribe((data) => {
      this.topNumbersByAll = data;

      for (const item of this.topNumbersByAll) {
        const faxNumber = this.faxPhoneNumbers.find(x => item.phoneNumber.indexOf(x.phoneNumber) > -1);
        if (faxNumber) {
          item.phoneNumberAlias = faxNumber.alias;
        }
      }

      this.topOutgoingNumbersByAll = this.topNumbersByAll.filter(x => x.direction === 'outgoing');
      this.topIncomingNumbersByAll = this.topNumbersByAll.filter(x => x.direction === 'incoming');
    });

    this.messagingPhoneNumbersSubscription = this.reportsService.faxPhoneNumbers.subscribe((data) => {
      this.faxPhoneNumbers = data;

      this.initFilterOptions();
    });

    await this.reportsService.getMyMessagingPhoneNumbers();
    await this.getSummaryData();
  }

  async getSummaryData() {
    // fixme this.headerService.showLoading();

    try {
      const filter: ReportRequest = {
        startDate: this.additionalFilter.startDate,
        endDate: this.additionalFilter.endDate,
        phoneNumbers: this.additionalFilter.phoneNumbers,
      };

      await this.reportsService.getSummaryList(filter);
      await this.reportsService.getSummaryListByDay(filter);
      await this.reportsService.getSummaryListByPhoneNumber(filter);

    } finally {
      // fixme this.headerService.hideLoading();
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.headerService.hideSearchInput();
    this.summarySubscription?.unsubscribe();
    this.summaryByDaySubscription?.unsubscribe();
    this.summaryByPhoneNumberSubscription?.unsubscribe();
    this.messagingPhoneNumbersSubscription?.unsubscribe();
  }

  onSeriesChartZoomClick() {
    this.dialog.open(MatFaxChartZoomDialogComponent, {
      width: '90%',
      height: '90%',
      data: {
        chartData: this.timeSeriesTotals,
        dialogTitle: `Message Breakdown`,
        chartType: this.timeSeriesType,
        startDate: this.additionalFilter.startDate,
        endDate: this.additionalFilter.endDate,
      }
    });
  }

  onFilterChanged(filter: any) {
    this.additionalFilter = filter;
    this.getSummaryData().then(() => {
      console.log('SummaryData fetched from server');
    });
  }

  showLogs($event: MouseEvent, type: 'incoming' | 'outgoing' | 'failed' | 'all') {
    this.dialog.open(FaxLogsDialogComponent, {
      width: '90%',
      height: '90%',
      data: {
        filter: {
          type: type,
          startDate: this.additionalFilter.startDate,
          endDate: this.additionalFilter.endDate,
          phoneNumbers: this.additionalFilter.phoneNumbers,
        }
      }
    });
  }

  initFilterOptions(): void {
    this.filterOptions = [];

    if (this.faxPhoneNumbers.length > 0) {
      for (const item of this.faxPhoneNumbers) {
        this.filterOptions.push({
          filterKey: 'phoneNumbers',
          multiValue: true,
          showAlways: false,
          type: 'static',
          title: 'Phone Number',
          displayValue: `${PhoneNumbersUtils.format(item.phoneNumber)} ${item.alias}`,
          value: item.phoneNumber,
          searchText: `Phone Number: ${PhoneNumbersUtils.phoneNumberToNational(item.phoneNumber)} ${item.alias}`,
        });
      }
    }
  }
}
